/**
 * A single-choice dropdown you can type into — the portal's replacement for
 * `<TextField select>`. Options are sorted alphabetically by label (unless
 * the list has a meaningful order of its own, e.g. time windows or lifecycle
 * stages — pass `keepOrder`), and an optional "none / all" choice stays
 * pinned at the top so it's never buried mid-list.
 */
import { useMemo, type ReactNode } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type { SxProps, Theme } from '@mui/material/styles';

export interface SelectOption<V extends string = string> {
  value: V;
  label: string;
  disabled?: boolean;
}

export function sortOptions<V extends string>(
  options: SelectOption<V>[],
): SelectOption<V>[] {
  return [...options].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base', numeric: true }),
  );
}

export function SearchableSelect<V extends string = string>({
  label,
  value,
  onChange,
  options,
  pinned,
  keepOrder = false,
  size = 'small',
  fullWidth,
  disabled,
  required,
  error,
  helperText,
  placeholder,
  sx,
  id,
  ariaLabel,
}: {
  label: string;
  value: V;
  onChange: (value: V) => void;
  options: SelectOption<V>[];
  /** Choices shown first, in the order given (e.g. "All departments"). */
  pinned?: SelectOption<V>[];
  /** The list already has a meaningful order — don't alphabetize it. */
  keepOrder?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  placeholder?: string;
  sx?: SxProps<Theme>;
  id?: string;
  /** Accessible name when several pickers share one visible label. */
  ariaLabel?: string;
}) {
  const all = useMemo(
    () => [...(pinned ?? []), ...(keepOrder ? options : sortOptions(options))],
    [pinned, options, keepOrder],
  );
  // A value with no matching option (still loading, or since removed) shows
  // as empty rather than throwing the Autocomplete into a warning loop.
  const selected = all.find((o) => o.value === value) ?? null;
  return (
    <Autocomplete<SelectOption<V>, false, boolean, false>
      id={id}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      sx={sx}
      options={all}
      value={selected}
      disableClearable={selected !== null}
      onChange={(_e, opt) => {
        if (opt) onChange(opt.value);
      }}
      getOptionLabel={(o) => o.label}
      getOptionDisabled={(o) => !!o.disabled}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      autoHighlight
      renderOption={(props, o) => {
        const { key, ...rest } = props as typeof props & { key: string };
        return (
          <li key={`${key}-${o.value}`} {...rest}>
            {o.label}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputProps={
            ariaLabel ? { ...params.inputProps, 'aria-label': ariaLabel } : params.inputProps
          }
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          placeholder={placeholder}
        />
      )}
    />
  );
}
